/**
 * Customer Orders API Client
 * API integration for customer order tracking and status updates
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-007
 */

import type {
  CustomerOrdersResponse,
  CustomerOrderFilters,
  OrderStatusInfo,
  DeliveryTrackingInfo,
  PaginationParams,
} from '../types/order.types';

// ============================================
// CONFIGURATION
// ============================================

const COMMERCE_SERVICE_BASE_URL =
  import.meta.env.VITE_COMMERCE_SERVICE_URL || 'http://localhost:8083/commerce-service';
const ORDER_API_PATH = '/api/v1/orders';

/**
 * Get JWT token from localStorage
 */
function getAuthToken(): string | null {
  const token = localStorage.getItem('saubhagya_jwt_token');
  return token;
}

/**
 * Get authorization headers with JWT token
 */
function getHeaders(): HeadersInit {
  const token = getAuthToken();
  const authHeader = token
    ? token.startsWith('Bearer ') ? token : `Bearer ${token}`
    : undefined;

  return {
    'Content-Type': 'application/json',
    ...(authHeader && { Authorization: authHeader }),
  };
}

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Custom error class for Customer Order API errors
 */
export class CustomerOrderApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errorCode?: string
  ) {
    super(message);
    this.name = 'CustomerOrderApiError';
  }
}

/**
 * Handle API response and errors
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = 'An error occurred';

    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } else {
        errorMessage = response.statusText || errorMessage;
      }
    } catch {
      errorMessage = response.statusText || errorMessage;
    }

    // Map status codes to user-friendly messages
    let userMessage = errorMessage;
    let errorCode = 'UNKNOWN_ERROR';

    switch (response.status) {
      case 401:
        userMessage = 'Please login to view your orders';
        errorCode = 'UNAUTHORIZED';
        break;
      case 403:
        userMessage = 'You do not have permission to access these orders';
        errorCode = 'FORBIDDEN';
        break;
      case 404:
        userMessage = 'Orders not found';
        errorCode = 'NOT_FOUND';
        break;
      case 500:
        userMessage = 'Server error. Please try again later';
        errorCode = 'SERVER_ERROR';
        break;
      case 503:
        userMessage = 'Service unavailable. Please try again later';
        errorCode = 'SERVICE_UNAVAILABLE';
        break;
    }

    console.error('[CustomerOrderApi] HTTP Error:', {
      status: response.status,
      errorCode,
      message: userMessage,
    });

    throw new CustomerOrderApiError(userMessage, response.status, errorCode);
  }

  try {
    return await response.json();
  } catch (parseError) {
    console.error('[CustomerOrderApi] JSON parse error:', parseError);
    throw new CustomerOrderApiError('Invalid response from server', 500, 'PARSE_ERROR');
  }
}

// ============================================
// API METHODS
// ============================================

/**
 * Customer Order API Client
 */
export const customerOrderApi = {
  /**
   * Get customer's own orders with filters and pagination
   *
   * @param pagination - Page and size parameters
   * @param filters - Optional filter criteria
   * @returns Paginated customer orders
   */
  async getMyOrders(
    pagination: PaginationParams,
    filters?: CustomerOrderFilters
  ): Promise<CustomerOrdersResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', pagination.page.toString());
      queryParams.append('size', pagination.size.toString());

      // Add filters
      if (filters) {
        if (filters.searchTerm) {
          queryParams.append('search', filters.searchTerm);
        }
        if (filters.statuses && filters.statuses.length > 0) {
          queryParams.append('statuses', filters.statuses.join(','));
        }
        if (filters.dateFrom) {
          queryParams.append('dateFrom', filters.dateFrom);
        }
        if (filters.dateTo) {
          queryParams.append('dateTo', filters.dateTo);
        }
        if (filters.sortBy) {
          queryParams.append('sortBy', filters.sortBy);
        }
        if (filters.sortDirection) {
          queryParams.append('sortDirection', filters.sortDirection);
        }
      }

      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/my-orders?${queryParams.toString()}`;
      console.log('[CustomerOrderApi] Fetching my orders:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      const data = await handleResponse<CustomerOrdersResponse>(response);
      console.log('[CustomerOrderApi] Successfully fetched orders:', {
        totalElements: data.totalElements,
        currentPage: data.currentPage,
        ordersCount: data.orders.length,
      });

      return data;
    } catch (error) {
      console.error('[CustomerOrderApi] Error fetching orders:', error);

      if (error instanceof CustomerOrderApiError) {
        throw error;
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new CustomerOrderApiError(
          'Cannot connect to order service. Please check your connection.',
          0,
          'NETWORK_ERROR'
        );
      }

      throw new CustomerOrderApiError(
        'Failed to fetch orders. Please try again.',
        500,
        'UNKNOWN_ERROR'
      );
    }
  },

  /**
   * Get status information for a specific order
   *
   * @param orderId - Order ID
   * @returns Order status information with timeline
   */
  async getOrderStatus(orderId: number): Promise<OrderStatusInfo> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/status`;
      console.log('[CustomerOrderApi] Fetching order status:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      const data = await handleResponse<OrderStatusInfo>(response);
      console.log('[CustomerOrderApi] Order status fetched:', {
        orderId: data.orderId,
        status: data.currentStatus,
      });

      return data;
    } catch (error) {
      console.error('[CustomerOrderApi] Error fetching order status:', error);

      if (error instanceof CustomerOrderApiError) {
        throw error;
      }

      throw new CustomerOrderApiError(
        'Failed to fetch order status. Please try again.',
        500,
        'UNKNOWN_ERROR'
      );
    }
  },

  /**
   * Get delivery information for an order
   * Only available when order status >= DISPATCHED
   *
   * @param orderId - Order ID
   * @returns Delivery tracking information
   */
  async getDeliveryInfo(orderId: number): Promise<DeliveryTrackingInfo> {
    try {
      const url = `${COMMERCE_SERVICE_BASE_URL}${ORDER_API_PATH}/${orderId}/delivery`;
      console.log('[CustomerOrderApi] Fetching delivery info:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: getHeaders(),
      });

      const data = await handleResponse<DeliveryTrackingInfo>(response);
      console.log('[CustomerOrderApi] Delivery info fetched:', {
        orderId: data.orderId,
        vehicleNumber: data.vehicleNumber,
      });

      return data;
    } catch (error) {
      console.error('[CustomerOrderApi] Error fetching delivery info:', error);

      if (error instanceof CustomerOrderApiError) {
        throw error;
      }

      throw new CustomerOrderApiError(
        'Failed to fetch delivery information. Please try again.',
        500,
        'UNKNOWN_ERROR'
      );
    }
  },

  /**
   * Check if order service is available
   *
   * @returns true if service is reachable
   */
  async checkHealth(): Promise<boolean> {
    try {
      const response = await fetch(
        `${COMMERCE_SERVICE_BASE_URL}/actuator/health`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      return response.ok;
    } catch {
      return false;
    }
  },
};

export default customerOrderApi;
